import React, { useState, useEffect } from "react";
import {
  Invoice,
  InvoiceService,
  InvoicePhoto,
  InvoiceMaterial,
  User,
} from "../types";
import {
  updateInvoice,
  getServices,
  getMaterials,
} from "../services/firebaseService";
import "./EditPage.css";

import {
  Service,
  normalizeServices,
  servicesToSaveShape,
} from "../types/service";

import { getCurrentFactorValue } from "../services/firebaseService";
import { log } from "console";

interface EditPageProps {
  // Empty interface for props
}

const EditPage: React.FC<EditPageProps> = () => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedBy, setEditedBy] = useState<string>("");
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number>(-1);

  // Service selection states
  const [serviceType, setServiceType] = useState<"nde" | "ndm">("ndm");
  const [availableServices, setAvailableServices] = useState<any[]>([]);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [loadingServices, setLoadingServices] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState<string>("");
  const [showServiceDropdown, setShowServiceDropdown] =
    useState<boolean>(false);

  // Material selection states
  const [availableMaterials, setAvailableMaterials] = useState<any[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<any>(null);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [materialSearchTerm, setMaterialSearchTerm] = useState<string>("");
  const [showMaterialDropdown, setShowMaterialDropdown] =
    useState<boolean>(false);

  // Form states
  const [serviceForm, setServiceForm] = useState({
    code: "",
    activity: "",
    description: "",
    quantity: 1,
    price: 0,
    factor: 1.6568,
    unid: "EA",
  });

  const [materialForm, setMaterialForm] = useState({
    descriptions: "",
    material: "",
    quantity: "1",
    action: "INSTALADO",
  });

  const [photoForm, setPhotoForm] = useState({
    firebasePath: "",
    pg: "",
    obs: "",
  });

  useEffect(() => {
    // Get invoice data from sessionStorage (set by parent window)
    const invoiceData = sessionStorage.getItem("invoiceData");
    const invoiceKey = sessionStorage.getItem("invoiceKey");
    const currentUserData = sessionStorage.getItem("currentUserData");

    console.log("EditPage - Raw invoiceData from sessionStorage:", invoiceData);

    if (invoiceData) {
      try {
        const parsedInvoice = JSON.parse(invoiceData);
        if (invoiceKey) {
          parsedInvoice.key = invoiceKey;
        }

        const sanitizedPhotos = Array.isArray(parsedInvoice.photos)
          ? parsedInvoice.photos.filter((p: any) => !!p)
          : [];

        const sanitizedMaterials = Array.isArray(parsedInvoice.materials)
          ? parsedInvoice.materials.filter((m: any) => !!m)
          : [];

        const sanitizedServices = normalizeServices(parsedInvoice.services);

        const sanitizedInvoice = {
          ...parsedInvoice,
          photos: sanitizedPhotos,
          materials: sanitizedMaterials,
          services: sanitizedServices,
        };

        console.log("EditPage - Parsed invoice:", sanitizedInvoice);
        setInvoice(sanitizedInvoice);
        setEditedBy(parsedInvoice.editedBy || "");
      } catch (error) {
        console.error("Error parsing invoice data:", error);
      }
    } else {
      console.log("EditPage - No invoiceData found in sessionStorage");
    }

    if (currentUserData) {
      try {
        const parsedUser = JSON.parse(currentUserData);
        setCurrentUser(parsedUser);
        console.log("EditPage - Current user loaded:", parsedUser);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }

    setLoading(false);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".searchable-dropdown")) {
        setShowServiceDropdown(false);
        setShowMaterialDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleHeaderChange = (field: string, value: string) => {
    if (!invoice) return;

    setInvoice((prev) => {
      const updatedInvoice = {
        ...prev!,
        header: {
          ...prev!.header,
          [field]: value,
        },
      };

      // If date is being changed, also update the root date field as timestamp
      if (field === "date") {
        try {
          // Only parse if we have a complete date string
          if (value && value.trim().length >= 10) {
            // Parse Brazilian date format (dd/mm/yyyy)
            const dateParts = value.trim().split("/");
            if (dateParts.length === 3) {
              const day = parseInt(dateParts[0], 10);
              const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed in JavaScript
              const year = parseInt(dateParts[2], 10);

              // Validate the parsed values
              if (
                !isNaN(day) &&
                !isNaN(month) &&
                !isNaN(year) &&
                day >= 1 &&
                day <= 31 &&
                month >= 0 &&
                month <= 11 &&
                year >= 1900 &&
                year <= 3000
              ) {
                const dateValue = new Date(Date.UTC(year, month, day));
                if (!isNaN(dateValue.getTime())) {
                  updatedInvoice.date = dateValue.getTime();
                }
              }
            }
          }
        } catch (error) {
          console.error("Error parsing Brazilian date:", error);
        }
      }

      return updatedInvoice;
    });
  };

  const handleSaveHeader = async () => {
    if (!invoice) return;

    setSaving(true);
    try {
      const updateData: any = {
        header: invoice.header,
        editedBy: currentUser?.name || "Unknown",
      };

      // If date was changed and is valid, also update the root date field
      if (
        invoice.date &&
        typeof invoice.date === "number" &&
        !isNaN(invoice.date)
      ) {
        updateData.date = invoice.date;
      }

      await updateInvoice(invoice.team, invoice.key, updateData);

      alert("Cabeçalho salvo com sucesso!");

      // Update session storage
      const updatedInvoice = {
        ...invoice,
        editedBy: currentUser?.name || "Unknown",
      };
      sessionStorage.setItem("invoiceData", JSON.stringify(updatedInvoice));
      setEditedBy(currentUser?.name || "Unknown");
    } catch (error) {
      console.error("Error saving header:", error);
      alert("Erro ao salvar cabeçalho: " + error);
    } finally {
      setSaving(false);
    }
  };

  const loadServicesByType = async (type: "nde" | "ndm") => {
    setLoadingServices(true);
    try {
      const services = await getServices(type);
      setAvailableServices(services);
    } catch (error) {
      console.error("Error loading services:", error);
      alert("Erro ao carregar serviços: " + error);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleServiceTypeChange = (type: "nde" | "ndm") => {
    setServiceType(type);
    setSelectedService(null);
    setServiceSearchTerm("");
    setShowServiceDropdown(false);
    setServiceForm({
      code: "",
      activity: "",
      description: "",
      quantity: 1,
      price: 0,
      factor: 1.6568,
      unid: "EA",
    });
    loadServicesByType(type);
  };

  const filteredServices = availableServices.filter((service) => {
    if (!serviceSearchTerm) return true;
    const searchLower = serviceSearchTerm.toLowerCase();
    return (
      service.code.toLowerCase().includes(searchLower) ||
      (service.activity && service.activity.toLowerCase().includes(searchLower))
    );
  });

  const filteredMaterials = availableMaterials.filter((material) => {
    if (!materialSearchTerm) return true;
    const searchLower = materialSearchTerm.toLowerCase();
    return (
      (material.descriptions &&
        material.descriptions.toLowerCase().includes(searchLower)) ||
      (material.material &&
        material.material.toString().toLowerCase().includes(searchLower))
    );
  });

  const handleServiceSelection = (service: any) => {
    console.log("Selected");
    console.log(service);
    setSelectedService(service);
    setServiceSearchTerm(`${service.code} - ${service.activity}`);
    setShowServiceDropdown(false);
    setServiceForm({
      code: service.code,
      activity: service.activity,
      description: service.description,
      quantity: 1,
      price: parseFloat(service.price) || 0,
      factor: parseFloat(service.factor) || 1.6568,
      unid: service.unid || "EA",
    });
  };

  const handleServiceSearchChange = (value: string) => {
    setServiceSearchTerm(value);
    setShowServiceDropdown(value.length > 0);
    if (value === "") {
      setSelectedService(null);
      setServiceForm({
        code: "",
        activity: "",
        description: "",
        quantity: 1,
        price: 0,
        factor: 1.6568,
        unid: "EA",
      });
    }
  };

  const loadMaterials = async () => {
    setLoadingMaterials(true);
    try {
      const materials = await getMaterials();
      setAvailableMaterials(materials);
    } catch (error) {
      console.error("Error loading materials:", error);
      alert("Erro ao carregar materiais: " + error);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleMaterialSelection = (material: any) => {
    setSelectedMaterial(material);
    setMaterialSearchTerm(`${material.descriptions} - ${material.material}`);
    setShowMaterialDropdown(false);
    setMaterialForm({
      descriptions: material.descriptions || "",
      material: material.material || "",
      quantity: "1",
      action: "INSTALADO",
    });
  };

  const handleMaterialSearchChange = (value: string) => {
    setMaterialSearchTerm(value);
    setShowMaterialDropdown(value.length > 0);
    if (value === "") {
      setSelectedMaterial(null);
      setMaterialForm({
        descriptions: "",
        material: "",
        quantity: "1",
        action: "INSTALADO",
      });
    }
  };

  const handleAddService = () => {
    setServiceForm({
      code: "",
      activity: "",
      description: "",
      quantity: 1,
      price: 0,
      factor: 1.6568,
      unid: "EA",
    });
    setSelectedService(null);
    setServiceSearchTerm("");
    setShowServiceDropdown(false);
    setServiceType("ndm");
    setEditingIndex(-1);
    setShowServiceModal(true);
    loadServicesByType("ndm");
  };

  const handleSaveService = async () => {
    if (!invoice) return;
    if (!serviceForm.code || !serviceForm.activity || serviceForm.price <= 0) {
      alert("Preencha todos os campos obrigatórios");
      return;
    }

    setSaving(true);
    try {
      const newService: Service = {
        code: serviceForm.code,
        activity: serviceForm.activity,
        description: serviceForm.description,
        quantity: serviceForm.quantity,
        price: serviceForm.price,
        factor: serviceForm.factor,
        unid: serviceForm.unid,
      };

      // normalize to array
      const arr = normalizeServices(invoice.services);

      if (editingIndex >= 0) {
        // update existing
        arr[editingIndex] = { ...arr[editingIndex], ...newService };
      } else {
        // add new
        arr.push(newService);
      }

      // calculate totals
      const totalWithoutFactor = arr.reduce((total, s) => {
        const price = Number(s.price) || 0;
        const qty = Number(s.quantity) || 0;
        return total + price * qty;
      }, 0);

      const totalWithFactor = arr.reduce((total, s) => {
        const price = Number(s.price) || 0;
        const qty = Number(s.quantity) || 0;
        const f = Number(s.factor);
        const factor =
          Number.isFinite(f) && f > 0 ? f : getCurrentFactorValue();
        return total + price * qty * factor;
      }, 0);

      // convert back to original shape to save
      const servicesToSave = servicesToSaveShape(arr, invoice.services);

      const updatedInvoice = {
        ...invoice,
        services: servicesToSave,
        total: totalWithoutFactor,
        totalWithFactor: totalWithFactor,
      };

      setInvoice(updatedInvoice);

      // Save to Firebase
      await updateInvoice(invoice.team, invoice.key, {
        services: servicesToSave,
        total: totalWithoutFactor,
        total_wfactor: totalWithFactor,
        editedBy: currentUser?.name || "Unknown",
      });

      const updatedInvoiceWithEditor = {
        ...updatedInvoice,
        editedBy: currentUser?.name || "Unknown",
      };
      sessionStorage.setItem(
        "invoiceData",
        JSON.stringify(updatedInvoiceWithEditor),
      );
      setEditedBy(currentUser?.name || "Unknown");

      setShowServiceModal(false);
      alert("Serviço salvo com sucesso!");
    } catch (error) {
      console.error("Error saving service:", error);
      alert("Erro ao salvar serviço: " + error);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveService = async (index: number) => {
    if (!invoice) return;
    if (!window.confirm("Deseja remover este serviço?")) return;

    setSaving(true);
    try {
      const arr = normalizeServices(invoice.services);
      arr.splice(index, 1);

      const totalWithoutFactor = arr.reduce((total, s) => {
        const price = Number(s.price) || 0;
        const qty = Number(s.quantity) || 0;
        return total + price * qty;
      }, 0);

      const totalWithFactor = arr.reduce((total, s) => {
        const price = Number(s.price) || 0;
        const qty = Number(s.quantity) || 0;
        const f = Number(s.factor);
        const factor =
          Number.isFinite(f) && f > 0 ? f : getCurrentFactorValue();
        return total + price * qty * factor;
      }, 0);

      const servicesToSave = servicesToSaveShape(arr, invoice.services);

      const updatedInvoice = {
        ...invoice,
        services: servicesToSave,
        total: totalWithoutFactor,
        totalWithFactor: totalWithFactor,
      };

      setInvoice(updatedInvoice);

      await updateInvoice(invoice.team, invoice.key, {
        services: servicesToSave,
        total: totalWithoutFactor,
        total_wfactor: totalWithFactor,
        editedBy: currentUser?.name || "Unknown",
      });

      const updatedInvoiceWithEditor = {
        ...updatedInvoice,
        editedBy: currentUser?.name || "Unknown",
      };
      sessionStorage.setItem(
        "invoiceData",
        JSON.stringify(updatedInvoiceWithEditor),
      );
      setEditedBy(currentUser?.name || "Unknown");

      alert("Serviço removido com sucesso!");
    } catch (error) {
      console.error("Error removing service:", error);
      alert("Erro ao remover serviço: " + error);
    } finally {
      setSaving(false);
    }
  };

  const servicesArray = normalizeServices(invoice?.services);

  const handleAddMaterial = () => {
    setMaterialForm({
      descriptions: "",
      material: "",
      quantity: "1",
      action: "INSTALADO",
    });
    setSelectedMaterial(null);
    setMaterialSearchTerm("");
    setShowMaterialDropdown(false);
    setEditingIndex(-1);
    setShowMaterialModal(true);
    loadMaterials();
  };

  const handleSaveMaterial = async () => {
    if (!invoice) return;
    if (
      !materialForm.descriptions ||
      !materialForm.material ||
      !materialForm.quantity
    ) {
      alert("Preencha todos os campos obrigatórios");
      return;
    }

    setSaving(true);
    try {
      const newMaterial: InvoiceMaterial = {
        descriptions: materialForm.descriptions,
        material: materialForm.material,
        quantity: materialForm.quantity,
        action: materialForm.action,
      };

      let updatedMaterials = invoice.materials ? [...invoice.materials] : [];

      // Add new material
      updatedMaterials.push(newMaterial);

      const updatedInvoice = {
        ...invoice,
        materials: updatedMaterials,
      };

      setInvoice(updatedInvoice);

      // Save to Firebase
      await updateInvoice(invoice.team, invoice.key, {
        materials: updatedMaterials,
        editedBy: currentUser?.name || "Unknown",
      });

      // Update session storage
      const updatedInvoiceWithEditor = {
        ...updatedInvoice,
        editedBy: currentUser?.name || "Unknown",
      };
      sessionStorage.setItem(
        "invoiceData",
        JSON.stringify(updatedInvoiceWithEditor),
      );
      setEditedBy(currentUser?.name || "Unknown");

      setShowMaterialModal(false);
      alert("Material salvo com sucesso!");
    } catch (error) {
      console.error("Error saving material:", error);
      alert("Erro ao salvar material: " + error);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMaterial = async (index: number) => {
    if (!invoice) return;

    if (!window.confirm("Deseja remover este material?")) return;

    setSaving(true);
    try {
      const updatedMaterials = invoice.materials
        ? invoice.materials.filter((_, i) => i !== index)
        : [];
      const updatedInvoice = {
        ...invoice,
        materials: updatedMaterials,
      };

      setInvoice(updatedInvoice);

      // Save to Firebase
      await updateInvoice(invoice.team, invoice.key, {
        materials: updatedMaterials,
        editedBy: currentUser?.name || "Unknown",
      });

      // Update session storage
      const updatedInvoiceWithEditor = {
        ...updatedInvoice,
        editedBy: currentUser?.name || "Unknown",
      };
      sessionStorage.setItem(
        "invoiceData",
        JSON.stringify(updatedInvoiceWithEditor),
      );
      setEditedBy(currentUser?.name || "Unknown");

      alert("Material removido com sucesso!");
    } catch (error) {
      console.error("Error removing material:", error);
      alert("Erro ao remover material: " + error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddPhoto = () => {
    setPhotoForm({
      firebasePath: "",
      pg: "",
      obs: "",
    });
    setEditingIndex(-1);
    setShowPhotoModal(true);
  };

  const handleSavePhoto = async () => {
    if (!invoice) return;
    if (!photoForm.pg || !photoForm.obs) {
      alert("Preencha todos os campos obrigatórios");
      return;
    }

    setSaving(true);
    try {
      const newPhoto: InvoicePhoto = {
        firebasePath: photoForm.firebasePath || "/placeholder.jpg",
        pg: photoForm.pg,
        obs: photoForm.obs,
      };

      let updatedPhotos = invoice.photos ? [...invoice.photos] : [];

      if (editingIndex >= 0) {
        // Edit existing photo
        updatedPhotos[editingIndex] = newPhoto;
      } else {
        // Add new photo
        updatedPhotos.push(newPhoto);
      }

      const updatedInvoice = {
        ...invoice,
        photos: updatedPhotos,
      };

      setInvoice(updatedInvoice);

      // Save to Firebase
      await updateInvoice(invoice.team, invoice.key, {
        photos: updatedPhotos,
        editedBy: currentUser?.name || "Unknown",
      });

      // Update session storage
      const updatedInvoiceWithEditor = {
        ...updatedInvoice,
        editedBy: currentUser?.name || "Unknown",
      };
      sessionStorage.setItem(
        "invoiceData",
        JSON.stringify(updatedInvoiceWithEditor),
      );
      setEditedBy(currentUser?.name || "Unknown");

      setShowPhotoModal(false);
      alert("Estrutura salva com sucesso!");
    } catch (error) {
      console.error("Error saving photo:", error);
      alert("Erro ao salvar estrutura: " + error);
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePhoto = async (index: number) => {
    if (!invoice) return;

    if (!window.confirm("Deseja remover esta estrutura?")) return;

    setSaving(true);
    try {
      const updatedPhotos = invoice.photos
        ? invoice.photos.filter((_, i) => i !== index)
        : [];
      const updatedInvoice = {
        ...invoice,
        photos: updatedPhotos,
      };

      setInvoice(updatedInvoice);

      // Save to Firebase
      await updateInvoice(invoice.team, invoice.key, {
        photos: updatedPhotos,
        editedBy: currentUser?.name || "Unknown",
      });

      // Update session storage
      const updatedInvoiceWithEditor = {
        ...updatedInvoice,
        editedBy: currentUser?.name || "Unknown",
      };
      sessionStorage.setItem(
        "invoiceData",
        JSON.stringify(updatedInvoiceWithEditor),
      );
      setEditedBy(currentUser?.name || "Unknown");

      alert("Estrutura removida com sucesso!");
    } catch (error) {
      console.error("Error removing photo:", error);
      alert("Erro ao remover estrutura: " + error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditPhoto = (index: number) => {
    const list = (invoice?.photos || []).filter((p) => !!p);
    const photo: any = list[index] || { firebasePath: "", pg: "", obs: "" };
    setPhotoForm({
      firebasePath: photo.firebasePath || "",
      pg: photo.pg || "",
      obs: photo.obs || "",
    });
    setEditingIndex(index);
    setShowPhotoModal(true);
  };

  const calculateTotal = () => {
    if (!invoice?.services) return 0;
    return invoice.services.reduce((total, service) => {
      const partialTotal =
        parseFloat(service.price.toString()) *
        parseFloat(service.quantity.toString()) *
        parseFloat(service.factor.toString());
      return total + partialTotal;
    }, 0);
  };

  if (loading) {
    return (
      <div className="edit-page-loading">
        <div className="loading-spinner"></div>
        <p>Carregando editor...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="edit-page-container">
        <div className="edit-error">
          <h2>Nenhum boletim selecionado</h2>
          <p>Selecione um boletim na tela anterior para editar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-page-container">
      <nav className="edit-navbar">
        <h4>Editor de Boletim</h4>
        <div className="edit-nav-info">
          <span>Última Edição: {editedBy || "N/A"}</span>
        </div>
      </nav>

      {/* Header Section */}
      <div className="edit-card">
        <div className="edit-card-header">
          <h4>Cabeçalho</h4>
        </div>
        <div className="edit-card-body">
          <div className="header-form">
            <table className="info-table-1">
              <tbody>
                <tr>
                  <td className="label-cell">
                    <strong>TÍTULO:</strong>
                  </td>
                  <td className="input-cell" colSpan={5}>
                    <input
                      type="text"
                      className="form-input"
                      value={invoice.header.title || ""}
                      onChange={(e) =>
                        handleHeaderChange("title", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label-cell">
                    <strong>PROJETO:</strong>
                  </td>
                  <td className="input-cell" colSpan={5}>
                    <input
                      type="text"
                      className="form-input"
                      value={invoice.header.project || ""}
                      onChange={(e) =>
                        handleHeaderChange("project", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label-cell">
                    <strong>MUNICÍPIO:</strong>
                  </td>
                  <td className="input-cell" colSpan={5}>
                    <input
                      type="text"
                      className="form-input"
                      value={invoice.header.city || ""}
                      onChange={(e) =>
                        handleHeaderChange("city", e.target.value)
                      }
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="info-table-2">
              <tbody>
                <tr>
                  <td className="label-cell">
                    <strong>DATA:</strong>
                  </td>
                  <td className="input-cell">
                    <input
                      type="text"
                      className="form-input"
                      value={invoice.header.date || ""}
                      onChange={(e) =>
                        handleHeaderChange("date", e.target.value)
                      }
                    />
                  </td>
                  <td className="label-cell">
                    <strong>S.E:</strong>
                  </td>
                  <td className="input-cell">
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).se || ""}
                      onChange={(e) => handleHeaderChange("se", e.target.value)}
                    />
                  </td>
                  <td className="label-cell">
                    <strong>OS:</strong>
                  </td>
                  <td className="input-cell">
                    <input
                      type="text"
                      className="form-input"
                      value={invoice.header.os || ""}
                      onChange={(e) => handleHeaderChange("os", e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label-cell">
                    <strong>SETOR:</strong>
                  </td>
                  <td className="input-cell" colSpan={5}>
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).sector || ""}
                      onChange={(e) =>
                        handleHeaderChange("sector", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label-cell">
                    <strong>PES:</strong>
                  </td>
                  <td className="input-cell">
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).si || ""}
                      onChange={(e) => handleHeaderChange("si", e.target.value)}
                    />
                  </td>
                  <td className="label-cell">
                    <strong>PMS:</strong>
                  </td>
                  <td className="input-cell">
                    <input
                      type="text"
                      className="form-input"
                      value={invoice.header.pms || ""}
                      onChange={(e) =>
                        handleHeaderChange("pms", e.target.value)
                      }
                    />
                  </td>
                  <td className="label-cell">
                    <strong>ALI:</strong>
                  </td>
                  <td className="input-cell">
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).ali || ""}
                      onChange={(e) =>
                        handleHeaderChange("ali", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label-cell">
                    <strong>RESP. COELBA:</strong>
                  </td>
                  <td className="input-cell" colSpan={2}>
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).respCoelba || ""}
                      onChange={(e) =>
                        handleHeaderChange("respCoelba", e.target.value)
                      }
                    />
                  </td>
                  <td className="label-cell">
                    <strong>RESP. ORCA:</strong>
                  </td>
                  <td className="input-cell" colSpan={2}>
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).respOrca || ""}
                      onChange={(e) =>
                        handleHeaderChange("respOrca", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label-cell">
                    <strong>OPER. H. REC.:</strong>
                  </td>
                  <td className="input-cell" colSpan={2}>
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).operHRec || ""}
                      onChange={(e) =>
                        handleHeaderChange("operHRec", e.target.value)
                      }
                    />
                  </td>
                  <td className="label-cell">
                    <strong>OPER. H. DEV.:</strong>
                  </td>
                  <td className="input-cell" colSpan={2}>
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).operHDev || ""}
                      onChange={(e) =>
                        handleHeaderChange("operHDev", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label-cell">
                    <strong>KM INICIAL:</strong>
                  </td>
                  <td className="input-cell" colSpan={2}>
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).kmInit || ""}
                      onChange={(e) =>
                        handleHeaderChange("kmInit", e.target.value)
                      }
                    />
                  </td>
                  <td className="label-cell">
                    <strong>KM FINAL:</strong>
                  </td>
                  <td className="input-cell" colSpan={2}>
                    <input
                      type="text"
                      className="form-input"
                      value={(invoice.header as any).kmEnd || ""}
                      onChange={(e) =>
                        handleHeaderChange("kmEnd", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label-cell">
                    <strong>OBSERVAÇÃO:</strong>
                  </td>
                  <td className="input-cell" colSpan={5}>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={(invoice.header as any).observation || ""}
                      onChange={(e) =>
                        handleHeaderChange("observation", e.target.value)
                      }
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <button
              className="btn btn-primary"
              onClick={handleSaveHeader}
              disabled={saving}
            >
              <i className="fas fa-save"></i>
              {saving ? "Salvando..." : "Salvar Cabeçalho"}
            </button>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="edit-card">
        <div className="edit-card-header">
          <h4>Serviços</h4>
          <button className="btn btn-primary" onClick={handleAddService}>
            <i className="fas fa-plus"></i> Adicionar Serviço
          </button>
        </div>
        <div className="edit-card-body">
          <table className="services-table">
            <thead>
              <tr>
                <th>CÓDIGO</th>
                <th>SERVIÇO</th>
                <th>QTD.</th>
                <th>VL.UNIT.</th>
                <th>FATOR</th>
                <th>TOTAL PARCIAL</th>
                <th>AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {servicesArray.map((service, index) => {
                const price = Number(service.price) || 0;
                const qty = Number(service.quantity) || 0;
                const fParsed = Number(service.factor);
                const factor =
                  Number.isFinite(fParsed) && fParsed > 0
                    ? fParsed
                    : getCurrentFactorValue();
                const partialTotal = (price * qty * factor).toFixed(2);

                return (
                  <tr key={service._key ?? index}>
                    <td>{service.code}</td>
                    <td>{service.activity || service.description}</td>
                    <td>{service.quantity ?? 0}</td>
                    <td>{service.price ?? 0}</td>
                    <td>{service.factor ?? factor}</td>
                    <td>{partialTotal}</td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveService(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className="total-label">
                  <strong>TOTAL</strong>
                </td>
                <td className="total-value">
                  <strong>{calculateTotal().toFixed(2)}</strong>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Photos Section */}
      <div className="edit-card">
        <div className="edit-card-header">
          <h4>Estruturas</h4>
          <button className="btn btn-primary" onClick={handleAddPhoto}>
            <i className="fas fa-plus"></i> Adicionar Estrutura
          </button>
        </div>
        <div className="edit-card-body">
          <table className="photos-table">
            <thead>
              <tr>
                <th>FOTO</th>
                <th>PG</th>
                <th>OBSERVAÇÃO</th>
                <th>AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {invoice.photos &&
                invoice.photos
                  .filter((p) => !!p)
                  .map((photo, index) => (
                    <tr key={index}>
                      <td>
                        <img
                          src={photo.firebasePath || "/placeholder.jpg"}
                          alt={`Estrutura ${index + 1}`}
                          width="150"
                          height="100"
                          style={{ objectFit: "cover" }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/placeholder.jpg";
                          }}
                        />
                      </td>
                      <td>{photo.pg}</td>
                      <td>{photo.obs}</td>
                      <td>
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => handleEditPhoto(index)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-danger btn-sm ml-2"
                          onClick={() => handleRemovePhoto(index)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
              {(!invoice.photos || invoice.photos.length === 0) && (
                <tr>
                  <td colSpan={4} className="text-center">
                    Nenhuma estrutura cadastrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Materials Section */}
      <div className="edit-card">
        <div className="edit-card-header">
          <h4>Materiais</h4>
          <button className="btn btn-primary" onClick={handleAddMaterial}>
            <i className="fas fa-plus"></i> Adicionar Material
          </button>
        </div>
        <div className="edit-card-body">
          <table className="materials-table">
            <thead>
              <tr>
                <th>DESCRIÇÃO</th>
                <th>CÓDIGO (MATERIAL)</th>
                <th>QUANTIDADE</th>
                <th>AÇÃO</th>
                <th>AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {invoice.materials &&
                invoice.materials.map((material, index) => (
                  <tr key={index}>
                    <td>{material.descriptions}</td>
                    <td>{material.material}</td>
                    <td>{material.quantity}</td>
                    <td>{material.action}</td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveMaterial(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              {(!invoice.materials || invoice.materials.length === 0) && (
                <tr>
                  <td colSpan={5} className="text-center">
                    Nenhum material cadastrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Service Modal */}
      {showServiceModal && (
        <div
          className="modal-backdrop"
          onClick={() => setShowServiceModal(false)}
        >
          <div
            className="modal-content service-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h5>Adicionar Serviço</h5>
              <button
                className="modal-close"
                onClick={() => setShowServiceModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Service Type Selection */}
              <div className="form-group">
                <label>
                  <strong>Tipo de Serviço:</strong>
                </label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="serviceType"
                      value="ndm"
                      checked={serviceType === "ndm"}
                      onChange={() => handleServiceTypeChange("ndm")}
                    />
                    <span>NDM</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="serviceType"
                      value="nde"
                      checked={serviceType === "nde"}
                      onChange={() => handleServiceTypeChange("nde")}
                    />
                    <span>NDE</span>
                  </label>
                </div>
              </div>

              {/* Service Selection */}
              {availableServices.length > 0 && (
                <div className="form-group">
                  <label>
                    <strong>Selecione o Serviço:</strong>
                  </label>
                  {loadingServices ? (
                    <div className="loading-services">
                      Carregando serviços...
                    </div>
                  ) : (
                    <div className="searchable-dropdown">
                      <input
                        type="text"
                        className="form-input"
                        value={serviceSearchTerm}
                        onChange={(e) =>
                          handleServiceSearchChange(e.target.value)
                        }
                        onFocus={() =>
                          setShowServiceDropdown(
                            serviceSearchTerm.length > 0 ||
                              availableServices.length > 0,
                          )
                        }
                        placeholder="Pesquise por código ou atividade..."
                      />
                      {showServiceDropdown && (
                        <div className="dropdown-options">
                          {filteredServices.length > 0 ? (
                            filteredServices.map((service) => (
                              <div
                                key={service.id}
                                className="dropdown-option"
                                onClick={() => handleServiceSelection(service)}
                              >
                                <strong>{service.code}</strong> -{" "}
                                {service.activity}
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-option disabled">
                              Nenhum serviço encontrado
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Service Details (Read-only when selected from database) */}
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <strong>Código:</strong>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={serviceForm.code}
                    onChange={(e) =>
                      setServiceForm({ ...serviceForm, code: e.target.value })
                    }
                    placeholder="Ex: NDMVB0073SC"
                    readOnly={selectedService !== null}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <strong>Unidade:</strong>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={serviceForm.unid}
                    onChange={(e) =>
                      setServiceForm({ ...serviceForm, unid: e.target.value })
                    }
                    placeholder="Ex: EA, M, KG"
                    readOnly={selectedService !== null}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <strong>Atividade:</strong>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={serviceForm.activity}
                  onChange={(e) =>
                    setServiceForm({ ...serviceForm, activity: e.target.value })
                  }
                  placeholder="Ex: SUBST ISOL E/OU PINO TANGENTE/ÂNG"
                  readOnly={selectedService !== null}
                />
              </div>

              <div className="form-group">
                <label>
                  <strong>Descrição:</strong>
                </label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={serviceForm.description}
                  onChange={(e) =>
                    setServiceForm({
                      ...serviceForm,
                      description: e.target.value,
                    })
                  }
                  placeholder="Descrição detalhada do serviço"
                  readOnly={selectedService !== null}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <strong>Preço Unitário:</strong>
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={serviceForm.price}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        price: parseFloat(e.target.value) || 0,
                      })
                    }
                    min="0"
                    step="0.01"
                    readOnly={selectedService !== null}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <strong>Fator:</strong>
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={serviceForm.factor}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        factor: parseFloat(e.target.value) || 1,
                      })
                    }
                    min="0"
                    step="0.0001"
                    readOnly={selectedService !== null}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <strong>Quantidade:</strong>
                  </label>
                  <input
                    type="number"
                    className="form-input quantity-input"
                    value={serviceForm.quantity}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        quantity: parseFloat(e.target.value) || 0,
                      })
                    }
                    min="0"
                    step="1"
                  />
                </div>
              </div>

              <div className="form-group">
                <strong>Total Parcial: </strong>
                <span className="total-preview">
                  {(
                    serviceForm.price *
                    serviceForm.quantity *
                    serviceForm.factor
                  ).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowServiceModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveService}
                disabled={saving}
              >
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Material Modal */}
      {showMaterialModal && (
        <div
          className="modal-backdrop"
          onClick={() => setShowMaterialModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Adicionar Material</h5>
              <button
                className="modal-close"
                onClick={() => setShowMaterialModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Material Selection */}
              {availableMaterials.length > 0 && (
                <div className="form-group">
                  <label>
                    <strong>Selecione o Material:</strong>
                  </label>
                  {loadingMaterials ? (
                    <div className="loading-services">
                      Carregando materiais...
                    </div>
                  ) : (
                    <div className="searchable-dropdown">
                      <input
                        type="text"
                        className="form-input"
                        value={materialSearchTerm}
                        onChange={(e) =>
                          handleMaterialSearchChange(e.target.value)
                        }
                        onFocus={() =>
                          setShowMaterialDropdown(
                            materialSearchTerm.length > 0 ||
                              availableMaterials.length > 0,
                          )
                        }
                        placeholder="Pesquise por descrição ou código do material..."
                      />
                      {showMaterialDropdown && (
                        <div className="dropdown-options">
                          {filteredMaterials.length > 0 ? (
                            filteredMaterials.map((material) => (
                              <div
                                key={material.id}
                                className="dropdown-option"
                                onClick={() =>
                                  handleMaterialSelection(material)
                                }
                              >
                                <strong>{material.descriptions}</strong> -{" "}
                                {material.material}
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-option disabled">
                              Nenhum material encontrado
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Material Details (Read-only when selected from database) */}
              <div className="form-group">
                <label>
                  <strong>Descrição:</strong>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={materialForm.descriptions}
                  onChange={(e) =>
                    setMaterialForm({
                      ...materialForm,
                      descriptions: e.target.value,
                    })
                  }
                  placeholder="Ex: ISOLADOR PORCELANA 34,5"
                  readOnly={selectedMaterial !== null}
                />
              </div>
              <div className="form-group">
                <label>
                  <strong>Código (Material):</strong>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={materialForm.material}
                  onChange={(e) =>
                    setMaterialForm({
                      ...materialForm,
                      material: e.target.value,
                    })
                  }
                  placeholder="Ex: 2314001"
                  readOnly={selectedMaterial !== null}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <strong>Quantidade:</strong>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={materialForm.quantity}
                    onChange={(e) =>
                      setMaterialForm({
                        ...materialForm,
                        quantity: e.target.value,
                      })
                    }
                    placeholder="Ex: 03"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <strong>Ação:</strong>
                  </label>
                  <select
                    className="form-input"
                    value={materialForm.action}
                    onChange={(e) =>
                      setMaterialForm({
                        ...materialForm,
                        action: e.target.value,
                      })
                    }
                  >
                    <option value="INSTALADO">INSTALADO</option>
                    <option value="RETIRADO">RETIRADO</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowMaterialModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveMaterial}
                disabled={saving}
              >
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {showPhotoModal && (
        <div
          className="modal-backdrop"
          onClick={() => setShowPhotoModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>
                {editingIndex >= 0 ? "Editar Estrutura" : "Adicionar Estrutura"}
              </h5>
              <button
                className="modal-close"
                onClick={() => setShowPhotoModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>
                  <strong>URL da Foto:</strong>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={photoForm.firebasePath}
                  onChange={(e) =>
                    setPhotoForm({ ...photoForm, firebasePath: e.target.value })
                  }
                  placeholder="URL da imagem ou deixe vazio para usar placeholder"
                />
              </div>
              <div className="form-group">
                <label>
                  <strong>PG:</strong>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={photoForm.pg}
                  onChange={(e) =>
                    setPhotoForm({ ...photoForm, pg: e.target.value })
                  }
                  placeholder="Ex: NO U198966"
                />
              </div>
              <div className="form-group">
                <label>
                  <strong>Observação:</strong>
                </label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={photoForm.obs}
                  onChange={(e) =>
                    setPhotoForm({ ...photoForm, obs: e.target.value })
                  }
                  placeholder="Observações sobre a estrutura"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPhotoModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSavePhoto}
                disabled={saving}
              >
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditPage;
